import classNames from 'classnames';
import React from 'react';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Col,
  Grid,
  Row,
  Tab,
  Tabs
} from 'react-bootstrap';
import { FiCheck, FiRefreshCw } from 'react-icons/fi';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../../constants';
import {
  isBusinessAdmin,
  isBusinessUser,
  isExternalUser,
  isTeamManager
} from '../../../../services';
import {
  businessActions,
  businessSelector
} from '../../../../state/modules/business';
import { taskActions } from '../../../../state/modules/task';
import {
  businessTaskSelector,
  tasksSelector,
  teamTaskSelector,
  getBusinessEmailJobStatusesSelector,
  skippedBusinessTaskUserReassessmentSelector,
  skippedTeamTaskUserReassessmentSelector
} from '../../../../state/modules/task/selectors';
import { currentUserSelector } from '../../../../state/modules/user/selectors';
import { Icon, InfoBanner } from '../../../components';
import ConfirmationModal from '../../../components/confirmation-modal';
import Loading from '../../loading';
import { taskTypes } from './config';
import CompleteTasksTable from './partials/completed-tasks-table';
import TasksToCompleteTable from './partials/tasks-to-complete-table';
import TeamTasksTable from './partials/team-tasks-table';
import he from 'he';

const STORAGE_KEYS = {
  SYNC_DISMISSED: 'caddie_sync_dismissed',
  DISMISSED_TASKS: 'caddie_dismissed_tasks'
};

const TAB_KEYS = {
  MY_TASKS: 'my-tasks',
  TEAM_TASKS: 'tasks-i-set',
  HISTORY: 'completed-tasks'
};

const STATE = {
  INITIALIZING: 'INITIALIZING',
  PROCESSING: 'PROCESSING',
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
};

/**
 * Main dashboard component managing configurations for personal tasks,
 * assigned team tasks, task histories, and background job status banners.
 */
const Home = ({
  tasksToComplete,
  businessTeamTasks,
  isBusinessAdmin,
  isTeamManager,
  skippedTaskUserReassessment,
  refetchData,
  setTaskType,
  setCpdAllocation,
  toggleDropdownOpen,
  isDropdownOpen,
  isFetchingTasks,
  isFetchingBusinessTasks,
  setTaskSuccess,
  businessEmailJobStatuses = [],

  businessSyncStatus,
  teamSyncStatus,
  isTriggeringBusinessSync,
  isTriggeringTeamSync,
  triggerBusinessSync,
  triggerTeamSync,
  scopedTeamManagement,
  business,

  activeTab,
  handleTabSelect,

  dismissedTaskIds,
  isSyncDismissed,
  dismissTaskBanner,
  dismissSyncBanner,
  resetSyncDismiss,
  removeTaskDismissal,

  showSyncOverrideModal,
  toggleSyncOverrideModal,

  is429Error,
  cleanTask,

  isLocallyTriggering,
  setLocallyTriggering
}) => {
  const unarchivedBusinessTeamTasks = businessTeamTasks.filter(
    ({ isArchived }) => !isArchived
  );

  const syncData = isBusinessAdmin ? businessSyncStatus : teamSyncStatus;
  const isTriggering = isBusinessAdmin
    ? isTriggeringBusinessSync
    : isTriggeringTeamSync;
  const hasSyncData = syncData && typeof syncData === 'object';

  const statusStr = hasSyncData ? syncData.status : syncData;
  const processed = hasSyncData ? syncData.processedCount : 0;
  const total = hasSyncData ? syncData.totalCount : 0;

  const percentage = total > 0 ? Math.floor((processed / total) * 100) : 0;

  const triggeredByEmail =
    hasSyncData && syncData.triggeredByEmail
      ? syncData.triggeredByEmail
      : 'System / Automated';

  const isSyncing =
    statusStr === STATE.INITIALIZING ||
    statusStr === STATE.PENDING ||
    statusStr === STATE.PROCESSING ||
    isTriggering;

  const showErrorHighlight = is429Error && isSyncing;

  const currentSyncLabel = isSyncing
    ? statusStr === STATE.PROCESSING && total > 0
      ? `Checking for task completion...`
      : 'Initializing task completion check...'
    : 'Check for task completion';

  const executeSyncImmediate = () => {
    setLocallyTriggering(true);
    resetSyncDismiss();
    cleanTask();

    if (isBusinessAdmin) {
      triggerBusinessSync(business.id);
    } else if (scopedTeamManagement) {
      triggerTeamSync(business.id, scopedTeamManagement.businessTeamId);
    }
  };

  const handleSyncButtonClick = () => {
    if (isSyncing) {
      toggleSyncOverrideModal();
    } else {
      executeSyncImmediate();
    }
  };

  if (isSyncing) {
    localStorage.removeItem(STORAGE_KEYS.SYNC_DISMISSED);
  }

  const visibleEmailJobs = (businessEmailJobStatuses || []).filter((job) => {
    if (job.status === STATE.PENDING || job.status === STATE.PROCESSING) {
      return true;
    }
    return !dismissedTaskIds.includes(String(job.targetId));
  });

  const persistentSyncDismissed =
    localStorage.getItem(STORAGE_KEYS.SYNC_DISMISSED) === 'true';
  const shouldHideSyncBanner = isSyncDismissed || persistentSyncDismissed;

  const isSyncBannerCompleted =
    !isSyncing && statusStr === STATE.COMPLETED && !shouldHideSyncBanner;

  const syncBannerElement = isSyncing ? (
    <InfoBanner
      key='sync-progress'
      success={true}
      local={true}
      info={
        <div className='banner-row-wrapper'>
          <div className='banner-text-centered'>
            <FiRefreshCw className='right-gap task-spin-icon' />
            {statusStr === STATE.PROCESSING && total > 0
              ? `Initiated by: [${triggeredByEmail}]. ${percentage}% completed. Checking task completion...`
              : `Initiated by: [${triggeredByEmail}]. Initializing task completion check...`}
          </div>
        </div>
      }
    />
  ) : statusStr === STATE.COMPLETED && !shouldHideSyncBanner ? (
    <InfoBanner
      key='sync-success'
      success={true}
      local={true}
      info={
        <div className='banner-row-wrapper'>
          <div className='banner-text-centered'>
            <FiCheck className='right-gap' />
            Task completion check finished successfully. All tasks are up to
            date!
          </div>
          <button
            onClick={dismissSyncBanner}
            className='banner-close-btn'
            title='Dismiss'
          >
            &times;
          </button>
        </div>
      }
    />
  ) : null;

  const emailJobsCompletedBanners = visibleEmailJobs
    .filter((job) => job.status === STATE.COMPLETED)
    .map((job) => {
      const taskId = String(job.targetId);
      const taskName = he.decode(String(job.taskName));
      return (
        <InfoBanner
          key={`job-success-${job.id}`}
          success={true}
          local={true}
          info={
            <div className='banner-row-wrapper'>
              <div className='banner-text-centered'>
                <FiCheck className='right-gap' />
                {`Successfully dispatched all notifications for task [${taskName}]!`}
              </div>
              <button
                onClick={() => dismissTaskBanner(taskId)}
                className='banner-close-btn'
                title='Dismiss'
              >
                &times;
              </button>
            </div>
          }
        />
      );
    });

  const emailJobsInProgressBanners = visibleEmailJobs
    .filter((job) => job.status !== STATE.COMPLETED)
    .map((job) => {
      const taskId = String(job.targetId);
      const taskName = String(job.taskName);

      if (job.status === STATE.PENDING || job.status === STATE.PROCESSING) {
        return (
          <InfoBanner
            key={`job-progress-${job.id}`}
            success={true}
            local={true}
            info={
              <div className='banner-row-wrapper'>
                <div className='banner-text-centered'>
                  <FiRefreshCw className='right-gap task-spin-icon' />
                  {`${job.percentage}% completed. Dispatching notifications for task [${taskName}]...`}
                </div>
              </div>
            }
          />
        );
      }

      if (job.status === STATE.FAILED) {
        return (
          <InfoBanner
            key={`job-failed-${job.id}`}
            warn={true}
            local={true}
            info={
              <div className='banner-row-wrapper'>
                <div className='banner-text-centered'>
                  {`Background notification dispatch failed for Task #${taskId}. Please check system logs.`}
                </div>
                <button
                  onClick={() => dismissTaskBanner(taskId)}
                  className='banner-close-btn'
                  title='Dismiss'
                >
                  &times;
                </button>
              </div>
            }
          />
        );
      }
      return null;
    })
    .filter(Boolean);

  const legacyFallbackBanner = setTaskSuccess
    ? [
        <InfoBanner
          key='legacy-fallback'
          success={setTaskSuccess.success}
          local={true}
          info={
            <div className='banner-row-wrapper'>
              <div className='banner-text-centered'>
                <FiCheck className='right-gap' />
                {setTaskSuccess.info}
              </div>
            </div>
          }
        />
      ]
    : [];

  const activeBanners = [
    ...(isSyncBannerCompleted && syncBannerElement ? [syncBannerElement] : []),
    ...legacyFallbackBanner,
    ...emailJobsCompletedBanners,
    ...(!isSyncBannerCompleted && syncBannerElement ? [syncBannerElement] : []),
    ...emailJobsInProgressBanners
  ];

  return (
    <div className='base-layout'>
      {activeBanners.length > 0 && (
        <div className='dashboard-notification-stack'>{activeBanners}</div>
      )}
      <section id='header--tasks' className='layout--header'>
        <Grid>
          <Row className='no-gutter align-items-center'>
            <Col xs={12} md={5}>
              <Breadcrumb>
                <LinkContainer to={routes.app.tasks.home}>
                  <BreadcrumbItem active>Tasks</BreadcrumbItem>
                </LinkContainer>
              </Breadcrumb>
              <h1>
                <Icon icon='training-large' /> Tasks
              </h1>
            </Col>

            <Col
              xs={12}
              md={7}
              className='top-gap-sm action-button-container text-right'
            >
              {(isBusinessAdmin || isTeamManager) &&
              activeTab === TAB_KEYS.TEAM_TASKS &&
              (skippedTaskUserReassessment || isSyncing) ? (
                <Button
                  onClick={handleSyncButtonClick}
                  disabled={isLocallyTriggering}
                  className={classNames('sync-action-btn', {
                    'sync-loading': isSyncing || isLocallyTriggering
                  })}
                >
                  <FiRefreshCw
                    className={classNames('right-gap', {
                      'task-spin-icon': isSyncing || isLocallyTriggering
                    })}
                  />
                  {currentSyncLabel}
                </Button>
              ) : null}

              {isBusinessAdmin ? (
                <LinkContainer
                  onClick={() => {
                    setTaskType(taskTypes.CADDIE);
                    setCpdAllocation(true);
                  }}
                  to={routes.app.tasks.details}
                  className='link-container-auto-width'
                >
                  <Button>
                    Create New Task <Icon icon='add-large' />
                  </Button>
                </LinkContainer>
              ) : null}
            </Col>
          </Row>
        </Grid>
      </section>
      <section id='section--tasks' className='layout--section'>
        <Grid>
          <Row className='no-gutter'>
            <Col className='task-table-tabs'>
              <Tabs
                id='task-tabs'
                activeKey={activeTab}
                onSelect={handleTabSelect}
                className={classNames('tab-zone', { opened: isDropdownOpen })}
              >
                <Tab
                  eventKey={TAB_KEYS.MY_TASKS}
                  title={
                    <div>
                      My Tasks
                      {tasksToComplete.length > 0 ? (
                        <Badge id='tasks-count'>{tasksToComplete.length}</Badge>
                      ) : null}
                      <span
                        className='activeArrow'
                        onClick={toggleDropdownOpen}
                      >
                        <i className='icon-chevron-down-large' />
                      </span>
                    </div>
                  }
                >
                  <Row className='no-gutter'>
                    <Col>
                      <TasksToCompleteTable
                        isFetching={isFetchingTasks || isSyncing}
                        data={tasksToComplete}
                      />
                    </Col>
                  </Row>
                </Tab>
                {isBusinessAdmin || isTeamManager ? (
                  <Tab
                    eventKey={TAB_KEYS.TEAM_TASKS}
                    title={
                      <div>
                        {`My teams' tasks`}
                        {unarchivedBusinessTeamTasks.length > 0 ? (
                          <Badge id='tasks-count'>
                            {unarchivedBusinessTeamTasks.length}
                          </Badge>
                        ) : null}
                        <span
                          className='activeArrow'
                          onClick={toggleDropdownOpen}
                        >
                          <i className='icon-chevron-down-large' />
                        </span>
                      </div>
                    }
                  >
                    <TeamTasksTable
                      editable={isBusinessAdmin}
                      isFetching={
                        isFetchingBusinessTasks || isFetchingTasks || isSyncing
                      }
                      data={businessTeamTasks}
                      callback={refetchData}
                      business={business}
                      removeTaskDismissal={removeTaskDismissal}
                    />
                  </Tab>
                ) : null}
                <Tab
                  eventKey={TAB_KEYS.HISTORY}
                  title={
                    <div>
                      My Task History
                      <span
                        className='activeArrow'
                        onClick={toggleDropdownOpen}
                      >
                        <i className='icon-chevron-down-large' />
                      </span>
                    </div>
                  }
                >
                  <br />
                  <CompleteTasksTable />
                </Tab>
              </Tabs>
            </Col>
          </Row>
        </Grid>
      </section>
      <ConfirmationModal
        key='sync-override-modal'
        show={showSyncOverrideModal}
        cancel={toggleSyncOverrideModal}
        headingText={<span>Task Completion Check in Progress</span>}
        cancelLabel='Keep Monitoring Live Feed'
        isLoading={isTriggering}
        actions={[
          {
            onConfirmAction: executeSyncImmediate,
            actionLabel: 'Force new check'
          }
        ]}
        message={
          <div className='sync-modal-wrapper'>
            <div
              className={classNames(
                'top-gap-xs bottom-gap-sm sync-modal-message-text',
                {
                  'popout-error-text': showErrorHighlight
                }
              )}
              key={
                showErrorHighlight ? `error-${isTriggering}` : 'normal-message'
              }
            >
              <span>
                A task completion check is already running for your{' '}
                {`${isBusinessAdmin ? 'business' : 'team'}`}. Please allow it to
                finish naturally.
              </span>
            </div>
            <strong className='sync-modal-subheading'>
              Active Check Details:
            </strong>
            <div className='top-gap-xs shift-up-xs sync-modal-details-box'>
              <ul className='no-margin sync-modal-metrics-list'>
                <li>
                  <strong>Initiated By:</strong>{' '}
                  <span className='sync-modal-label-bold'>
                    {triggeredByEmail}
                  </span>
                </li>
                <li>
                  <strong>Status:</strong>{' '}
                  <span className='sync-modal-status-highlight'>
                    {statusStr || STATE.INITIALIZING}
                  </span>
                </li>
                <li>
                  <strong>Progress:</strong> {percentage}% completed
                </li>
              </ul>
            </div>
            <div className='sync-modal-footer-text'>
              The progress bar refreshes every few seconds. You should only use
              <strong>FORCE NEW CHECK</strong> if the progress bar freezes
              completely and fails to move for more than 2 minutes.
            </div>
          </div>
        }
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  tasksToComplete: tasksSelector(state),
  isBusinessAdmin: isBusinessAdmin(state.user.currentUser),
  isBusinessUser: isBusinessUser(state.user.currentUser),
  isExternalUser: isExternalUser(state.user.currentUser),
  business: businessSelector(state),
  user: currentUserSelector(state),
  businessTasks: businessTaskSelector(state),
  teamTasks: teamTaskSelector(state),
  skippedBusinessTaskUserReassessment:
    skippedBusinessTaskUserReassessmentSelector(state),
  skippedTeamTaskUserReassessment:
    skippedTeamTaskUserReassessmentSelector(state),
  isFetchingTasks: state.task.isFetching,
  isFetchingBusinessTasks: state.task.isFetchingBusinessTasks,
  setTaskSuccess: state.task.setTaskSuccess,
  businessEmailJobStatuses: getBusinessEmailJobStatusesSelector(state),

  businessSyncStatus: state.task.businessSyncStatus,
  teamSyncStatus: state.task.teamSyncStatus,
  isTriggeringBusinessSync: state.task.isTriggeringBusinessSync,
  isTriggeringTeamSync: state.task.isTriggeringTeamSync,

  is429Error: state.task.is429Error
});

const mapDispatchToProps = (dispatch) => ({
  getTasks: bindActionCreators(taskActions.task.getTasks, dispatch),
  setTaskType: bindActionCreators(taskActions.task.chooseTaskType, dispatch),
  setCpdAllocation: bindActionCreators(
    taskActions.task.setCpdAllocation,
    dispatch
  ),
  cleanTask: bindActionCreators(taskActions.task.cleanTask, dispatch),
  getBusinessTasks: bindActionCreators(
    taskActions.task.getBusinessTasks,
    dispatch
  ),
  getTeamTasks: bindActionCreators(taskActions.task.getTeamTasks, dispatch),
  getTaskCount: bindActionCreators(taskActions.task.getCount, dispatch),
  getTeamMembers: bindActionCreators(
    businessActions.business.team.getTeamMembers,
    dispatch
  ),

  triggerBusinessSync: bindActionCreators(
    taskActions.task.triggerBusinessSync,
    dispatch
  ),
  getBusinessSyncStatus: bindActionCreators(
    taskActions.task.getBusinessSyncStatus,
    dispatch
  ),
  triggerTeamSync: bindActionCreators(
    taskActions.task.triggerTeamSync,
    dispatch
  ),
  getTeamSyncStatus: bindActionCreators(
    taskActions.task.getTeamSyncStatus,
    dispatch
  )
});

/**
 * Binds domain methods and properties for localized team configurations
 * or global enterprise operations based on user role assignments.
 */
export const mapTaskDashboardProps = ({
  getTasks,
  cleanTask,
  getTaskCount,
  isBusinessAdmin,
  getBusinessTasks,
  getTeamTasks,
  businessTasks,
  teamTasks,
  skippedBusinessTaskUserReassessment,
  skippedTeamTaskUserReassessment,
  business,
  user
}) => {
  const scopedManagerRole = isTeamManager(user, business);
  const hasManagedTeamForThisBusiness = !!scopedManagerRole;

  return {
    businessTeamTasks: isBusinessAdmin ? businessTasks : teamTasks,
    isTeamManager: hasManagedTeamForThisBusiness,
    scopedTeamManagement: scopedManagerRole,
    skippedTaskUserReassessment: isBusinessAdmin
      ? skippedBusinessTaskUserReassessment
      : skippedTeamTaskUserReassessment,

    refetchData: (filter = {}) => {
      getTaskCount();
      cleanTask();
      getTasks();

      if (isBusinessAdmin) {
        getBusinessTasks(business.id, filter);
      } else if (hasManagedTeamForThisBusiness && scopedManagerRole) {
        getTeamTasks(business.id, scopedManagerRole.businessTeamId, filter);
      }
    }
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ isBusinessAdmin, isBusinessUser, isExternalUser }) =>
      !(isBusinessAdmin || isBusinessUser || isExternalUser),
    renderComponent(Loading)
  ),
  withProps(mapTaskDashboardProps),

  /** Configures initial functional view conditions and local storage mapping structures. */
  withStateHandlers(
    (props) => ({
      isDropdownOpen: false,
      activeTab: TAB_KEYS.MY_TASKS,
      showSyncOverrideModal: false,
      isLocallyTriggering: false,
      dismissedTaskIds: (() => {
        try {
          const cached =
            JSON.parse(localStorage.getItem(STORAGE_KEYS.DISMISSED_TASKS)) ||
            [];
          return cached.map(String);
        } catch (e) {
          return [];
        }
      })(),
      isSyncDismissed:
        localStorage.getItem(STORAGE_KEYS.SYNC_DISMISSED) === 'true'
    }),
    {
      setLocallyTriggering: () => (val) => ({
        isLocallyTriggering: val
      }),
      toggleDropdownOpen:
        ({ isDropdownOpen }) =>
        () => ({
          isDropdownOpen: !isDropdownOpen
        }),
      handleTabSelect: () => (key) => ({
        activeTab: key
      }),
      toggleSyncOverrideModal:
        ({ showSyncOverrideModal }) =>
        () => ({
          showSyncOverrideModal: !showSyncOverrideModal
        }),
      dismissTaskBanner:
        ({ dismissedTaskIds }) =>
        (taskId) => {
          const updated = [...dismissedTaskIds, String(taskId)];
          localStorage.setItem(
            STORAGE_KEYS.DISMISSED_TASKS,
            JSON.stringify(updated)
          );
          return { dismissedTaskIds: updated };
        },
      removeTaskDismissal:
        ({ dismissedTaskIds }) =>
        (taskId) => {
          const updated = dismissedTaskIds.filter(
            (id) => id !== String(taskId)
          );
          localStorage.setItem(
            STORAGE_KEYS.DISMISSED_TASKS,
            JSON.stringify(updated)
          );
          return { dismissedTaskIds: updated };
        },
      dismissSyncBanner: () => () => {
        localStorage.setItem(STORAGE_KEYS.SYNC_DISMISSED, 'true');
        return { isSyncDismissed: true };
      },
      resetSyncDismiss: () => () => {
        localStorage.removeItem(STORAGE_KEYS.SYNC_DISMISSED);
        return { isSyncDismissed: false };
      }
    }
  ),

  /** Manages initialization queries and updates interaction metrics when handling API mutations. */
  lifecycle({
    componentDidMount() {
      const {
        refetchData,
        scopedTeamManagement,
        business,
        getTeamMembers,
        getBusinessSyncStatus,
        getTeamSyncStatus,
        isBusinessAdmin
      } = this.props;

      refetchData();

      if (isBusinessAdmin && business) {
        getBusinessSyncStatus(business.id, true);
      } else if (scopedTeamManagement && business) {
        const teamId = scopedTeamManagement.businessTeamId;
        getTeamMembers(business.id, teamId, { teamId });
        getTeamSyncStatus(business.id, teamId, true);
      }
    },
    componentDidUpdate(prevProps) {
      const {
        isTriggeringBusinessSync,
        isTriggeringTeamSync,
        is429Error,
        showSyncOverrideModal,
        toggleSyncOverrideModal,
        isLocallyTriggering,
        setLocallyTriggering,
        businessSyncStatus,
        teamSyncStatus,
        isBusinessAdmin
      } = this.props;

      const wasTriggering =
        prevProps.isTriggeringBusinessSync || prevProps.isTriggeringTeamSync;
      const isTriggeringNow = isTriggeringBusinessSync || isTriggeringTeamSync;

      const syncData = isBusinessAdmin ? businessSyncStatus : teamSyncStatus;
      const hasSyncData = syncData && typeof syncData === 'object';
      const statusStr = hasSyncData ? syncData.status : syncData;
      const isSyncingNow =
        statusStr === STATE.INITIALIZING ||
        statusStr === STATE.PENDING ||
        statusStr === STATE.PROCESSING ||
        isTriggeringNow;

      if (
        isLocallyTriggering &&
        (isSyncingNow || is429Error || wasTriggering)
      ) {
        setLocallyTriggering(false);
      }

      if (
        wasTriggering &&
        !isTriggeringNow &&
        showSyncOverrideModal !== Boolean(is429Error)
      ) {
        toggleSyncOverrideModal();
      }
    }
  })
)(Home);



// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/home.js